import { datadogRum } from '@datadog/browser-rum';
import { logError } from '@ptek/client-logger';

/**
 * Reports a dropped/failed host-event payload. Goes to `logError` (console,
 * gated by log level) AND unconditionally to Datadog RUM via `addError`, which
 * is not silenced by the client-logger level — so a host running with logging
 * off still surfaces the drop in RUM. Only bounded, non-sensitive shape
 * metadata is emitted (`payloadType` + `payloadKeyCount`) — never the raw
 * payload, its values, OR its top-level key strings. A malformed payload is
 * `unknown`, so its keys themselves could carry PHI (e.g. a map keyed by
 * patient names or note text); reporting the count keeps telemetry safe.
 *
 * Kept in its own module (rather than dd-rum.ts) so importing it does not pull
 * in dd-rum.ts's eager top-level RUM config evaluation.
 */
export const reportMalformedHostPayload = (
  context: string,
  message: string,
  payload: unknown
): void => {
  const summary = {
    context,
    payloadType: Array.isArray(payload) ? 'array' : typeof payload,
    payloadKeyCount:
      payload && typeof payload === 'object' && !Array.isArray(payload)
        ? Object.keys(payload as Record<string, unknown>).length
        : undefined,
  };

  logError(context, message, summary);

  try {
    datadogRum.addError(new Error(`[${context}] ${message}`), summary);
  } catch (error) {
    logError(context, 'Failed to report malformed payload to RUM:', error);
  }
};
